/**
 * SHARE: the cave's code and link (docs/spec/03 §6), copied to the clipboard when the browser
 * allows. Pasting a code or link here (Ctrl+V) opens that cave instead.
 */

import { decodeCave } from '../../../caves/codec';
import type { CaveDef } from '../../../caves/types';
import { GREY, LIGHT_BLUE } from '../../../render/palette';
import type { KeyInput } from '../../../ui/scene';
import type { KitApi } from '../api';
import type { Rect } from '../layout';
import { INK } from '../layout';
import { button, dialog, fit } from '../widgets';
import { drawCode, LOWER_INK } from './codeText';
import type { Modal } from './modal';

const COLS = 66;
const MAX_LINES = 11;

/** The link that opens a code: this page with `?cave=`. */
export function shareLink(code: string): string {
  return `${location.origin}${location.pathname}?cave=${code}`;
}

function chunks(text: string, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < text.length; i += n) out.push(text.slice(i, i + n));
  return out;
}

export function shareModal(k: KitApi, def: CaveDef, code: string, open: (def: CaveDef) => void): Modal {
  const link = shareLink(code);
  let status = { text: 'COPYING THE LINK...', ink: GREY as number };
  const copy = (what: 'link' | 'code'): void => {
    const text = what === 'link' ? link : code;
    const done = (ok: boolean): void => {
      status = ok
        ? { text: `THE ${what.toUpperCase()} IS ON YOUR CLIPBOARD`, ink: INK.ok }
        : { text: 'THE BROWSER WOULD NOT COPY IT: TRY THE BUTTON AGAIN', ink: INK.warning };
      k.host.services.announce(status.text);
    };
    try {
      const clip = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
      if (!clip) return done(false);
      clip.writeText(text).then(() => done(true), () => done(false));
    } catch {
      done(false);
    }
  };
  copy('link');
  const lines = chunks(code, COLS);
  return {
    draw(ui) {
      const shown = Math.min(lines.length, MAX_LINES);
      const h = 118 + shown * 10;
      const r = dialog(ui, 560, h, `SHARE ${def.name}`);
      const s = ui.screen;
      s.text(`THE LINK OPENS IT IN STONEFALL (${code.length} CHARACTERS OF CODE):`, r.x + 12, r.y + 17, INK.text);
      s.text(fit(`${location.origin}${location.pathname}?cave=`, 46), r.x + 12, r.y + 31, LIGHT_BLUE);
      s.text('LOWER CASE IN CYAN', r.x + r.w - 12, r.y + 31, LOWER_INK, 'right');
      lines.slice(0, shown).forEach((line, i) => drawCode(s, line, r.x + 12, r.y + 43 + i * 10));
      const more = lines.length - shown;
      if (more > 0) s.text(`... (${more} MORE LINES)`, r.x + 12, r.y + 43 + shown * 10, GREY);
      const y = r.y + 50 + shown * 10;
      s.text(status.text, r.x + 12, y + 4, status.ink);
      s.text('TO OPEN SOMEONE ELSE\'S CAVE, PASTE ITS CODE OR LINK HERE (CTRL+V).', r.x + 12, y + 18, INK.dim);
      const by = r.y + h - 26;
      const at = (x: number, w = 150): Rect => ({ x: r.x + x, y: by, w, h: 16 });
      button(ui, at(12), 'COPY LINK (L)', { id: 'copy-link', tip: 'COPY THE LINK', click: () => copy('link') });
      button(ui, at(170), 'COPY CODE (C)', { id: 'copy-code', tip: 'COPY THE CODE ALONE', click: () => copy('code') });
      button(ui, at(r.w - 112, 100), 'CLOSE', { id: 'close', tip: 'ESC', click: () => k.closeModal() });
    },
    key(key: KeyInput) {
      if (key.code === 'KeyL') copy('link');
      else if (key.code === 'KeyC' && !key.ctrl) copy('code');
      else if (key.code === 'Escape' || key.code === 'Enter' || key.code === 'Backspace') k.closeModal();
    },
    paste(text) {
      const got = decodeCave(text);
      if (!got.ok) {
        status = { text: fit(`THAT CODE DID NOT WORK: ${got.reason.toUpperCase()}`, COLS), ink: INK.error };
        k.sound('error');
        return;
      }
      k.closeModal();
      open(got.def);
    },
  };
}
